import { useState } from 'react';
import { FileText, GitCommit } from 'lucide-react';
import { FilterChipGroup } from '@/components/ui/filter-chip.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';
import { useGetArtefacts } from '@edison/api-client-react';
import { cn } from '@/lib/utils.js';

const FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'documents', label: 'Documents' },
  { key: 'engagement', label: 'Engagement' },
];

const DOCUMENT_TYPES = ['meeting_transcript', 'prd_document', 'manual_note', 'file_upload', 'llm_session'];
const ENGAGEMENT_TYPES = ['github_commit', 'github_pr', 'github_issue'];

function typeToFilterKey(type: string): string {
  if (DOCUMENT_TYPES.includes(type)) return 'documents';
  if (ENGAGEMENT_TYPES.includes(type)) return 'engagement';
  return 'documents';
}

function typeIcon(type: string): typeof FileText {
  if (ENGAGEMENT_TYPES.includes(type)) return GitCommit;
  return FileText;
}

const WHY_IT_MATTERS: Record<string, string> = {
  github_commit: 'Demonstrates systematic investigation — traceable code change with R&D purpose.',
  github_pr: 'Evidence of collaborative technical review and iterative hypothesis testing.',
  meeting_transcript: 'Captures contemporaneous discussion of technical uncertainty and approach.',
  prd_document: 'Defines the problem scope and links technical work to the R&D hypothesis.',
  llm_session: 'AI-assisted R&D session — demonstrating the experimental iteration cycle.',
};

interface TimelineTabProps {
  projectId: string;
}

export function TimelineTab({ projectId }: TimelineTabProps) {
  const [filter, setFilter] = useState('all');

  const { data, isLoading, error, refetch } = useGetArtefacts({ projectId });

  const artefacts = data?.data?.items ?? [];

  const sorted = [...artefacts].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const filtered = filter === 'all'
    ? sorted
    : sorted.filter(a => typeToFilterKey(a.type) === filter);

  return (
    <div className="space-y-4">
      <FilterChipGroup chips={FILTER_CHIPS} value={filter} onChange={setFilter} />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
        </div>
      ) : error ? (
        <InlineError message="Failed to load timeline." onRetry={() => refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-10 w-10" />}
          title="No timeline events yet"
          body={
            filter === 'all'
              ? 'Connect a GitHub repo or upload documents to build the project narrative.'
              : `No ${filter} events captured yet.`
          }
        />
      ) : (
        <div className="relative max-h-[560px] overflow-y-auto space-y-0 pr-1">
          {filtered.map((item, i) => {
            const Icon = typeIcon(item.type);
            const isEngagement = ENGAGEMENT_TYPES.includes(item.type);
            return (
              <div
                key={item.id}
                className={cn(
                  'flex gap-4 pb-6',
                  i < filtered.length - 1 &&
                    'border-l-2 border-[oklch(0.92_0.006_250)] ml-[11px] pl-6'
                )}
              >
                <div
                  className={cn(
                    'shrink-0 w-6 h-6 rounded-full border-2 border-background flex items-center justify-center -ml-3 mt-0.5',
                    isEngagement
                      ? 'bg-[oklch(0.93_0.015_250)]'
                      : 'bg-[oklch(0.94_0.04_275)]'
                  )}
                >
                  <Icon className="h-3 w-3 text-foreground" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {item.title ?? '(untitled)'}
                    </p>
                    <span className="text-xs text-muted-foreground font-mono shrink-0">
                      {new Date(item.createdAt).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    {WHY_IT_MATTERS[item.type] ??
                      'Supporting evidence for the R&D claim narrative.'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
